<template>
  <div id="app" :class="{ 'menu-open': menuOpen }">
    <a class="skip-link" href="#main">Skip to content</a>

    <header class="site-header" :class="{ 'site-header--scrolled': scrolled }">
      <a class="brand" href="#top" aria-label="Synfesys home" @click="closeMenu">
        <img src="@/assets/logo.png" class="brand-mark" alt="" aria-hidden="true">
      </a>

      <nav ref="siteNavigation" id="site-navigation" class="site-nav" aria-label="Primary navigation">
        <a
          v-for="item in navigation"
          :key="item.id"
          :href="`#${item.id}`"
          :class="{ active: activeSection === item.id }"
          @click="closeMenu"
        >
          {{ item.label }}
        </a>
      </nav>

      <a class="header-contact" href="mailto:sales@synfesys.com">Start a conversation</a>

      <button
        ref="menuToggle"
        class="menu-toggle"
        type="button"
        :aria-expanded="menuOpen.toString()"
        aria-controls="site-navigation"
        :aria-label="menuOpen ? 'Close navigation' : 'Open navigation'"
        @click="toggleMenu"
      >
        <span></span><span></span>
      </button>
    </header>

    <main ref="mainContent" id="main">
      <section id="top" class="section hero" data-section="top">
        <div class="stars stars--one" aria-hidden="true"></div>
        <div class="stars stars--two" aria-hidden="true"></div>
        <div class="hero-orbit" aria-hidden="true"></div>

        <div class="hero-content reveal">
          <h1><span>Enabling</span><span>Intelligence</span></h1>
          <p class="hero-copy">
            Building the infrastructure, platforms and partnerships that enable the next generation of intelligence.
          </p>
          <a class="button button--light" href="#vision">Explore Our Vision</a>
        </div>

        <div class="hero-meta" aria-hidden="true">
          <span>Singapore · Indonesia · Finland</span>
        </div>
      </section>

      <section id="vision" class="section statement" data-section="vision">
        <div class="section-shade" aria-hidden="true"></div>
        <div class="statement-copy reveal">
          <h2>Intelligence<br>doesn't scale<br>by itself</h2>
        </div>
      </section>

      <section id="infrastructure" class="section foundation" data-section="infrastructure">
        <div class="foundation-rings" aria-hidden="true"></div>
        <div class="foundation-copy reveal">
          <h2><span>It needs</span><span>infrastructure</span></h2>
          <div class="keywords" aria-label="Infrastructure foundations">
            <span>Compute</span><i></i><span>Energy</span><i></i><span>Connectivity</span><i></i><span>Capital</span><i></i><span>Partnerships</span>
          </div>
        </div>
      </section>

      <section id="platforms" class="section platforms" data-section="platforms">
        <div class="platforms-heading reveal">
          <h2>Our platforms</h2>
        </div>

        <div class="platform-grid">
          <article v-for="platform in platforms" :key="platform.title" class="platform-card reveal">
            <p class="platform-domain">{{ platform.domain }}</p>
            <div class="platform-content">
              <h3>{{ platform.title }}</h3>
              <p class="platform-copy">{{ platform.copy }}</p>
            </div>
          </article>
        </div>
      </section>

      <section id="asia" class="section asia" data-section="asia">
        <div class="asia-heading reveal">
          <h2>Asia Strategy</h2>
          <div class="asia-intro">
            <p>Asia will be the fastest-growing AI market of the next decade.</p>
            <p>Synfesys AI is building a regional platform connecting Singapore, Indonesia and Finland.</p>
          </div>
        </div>

        <div class="globe" aria-label="Synfesys Asia strategy map">
          <div class="map-point point--singapore reveal">
            <span class="point-dot"></span>
            <strong>Singapore</strong>
            <small>Regional hub</small>
          </div>
          <div class="map-point point--indonesia reveal">
            <span class="point-dot"></span>
            <strong>Indonesia</strong>
            <small>Growth market</small>
          </div>
          <div class="map-point point--future reveal">
            <span class="point-dot"></span>
            <strong>Finland</strong>
            <small>ACROSS EUROPE</small>
          </div>
        </div>
      </section>

      <section id="about" class="section about" data-section="about">
        <div class="about-panel reveal">
          <h2>About <span>Synfesys</span> <span>AI</span></h2>
          <div class="about-copy">
            <p>Synfesys AI  is a technology group building the infrastructure, platforms and partnerships that enable the future of artificial intelligence.</p>
            <p>We invest for the long term, operate across Asia and create businesses that power the AI economy.</p>
          </div>
        </div>
      </section>

      <section id="contact" class="contact" data-section="contact">
        <div class="contact-main reveal">
          <h2>Let's build the future<br>of intelligence</h2>
          <div class="contact-details">
            <span>Contact</span>
            <a href="mailto:sales@synfesys.com">sales@synfesys.com</a>
          </div>
        </div>

        <footer class="footer">
          <div class="footer-brand">
            <img src="@/assets/logo.png" class="brand-mark" alt="" aria-hidden="true">
          </div>
          <div class="footer-bottom">
            <span>© {{ currentYear }} SYNFESYS. All rights reserved.</span>
          </div>
        </footer>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'App',
  data () {
    return {
      menuOpen: false,
      scrolled: false,
      activeSection: 'top',
      navigation: [
        { id: 'vision', label: 'Vision' },
        { id: 'platforms', label: 'Platforms' },
        { id: 'asia', label: 'Asia' },
        { id: 'about', label: 'About' }
      ],
      platforms: [
        {
          title: 'AI Infrastructure',
          domain: 'Compute · Energy · Connectivity',
          copy: 'The foundations intelligence needs to scale.'
        },
        {
          title: 'Digital Platforms',
          domain: 'Live streaming · Digital products',
          copy: 'Connected experiences built for audiences everywhere.'
        },
        {
          title: 'Strategic Investments',
          domain: 'Capital · Technology · Growth',
          copy: 'Long-term capital aligned with meaningful technology.'
        },
        {
          title: 'Executive Missions',
          domain: 'Partnerships · Market access',
          copy: 'Strategic partnerships that move ideas into action.'
        }
      ],
      observers: []
    }
  },
  computed: {
    currentYear () {
      return new Date().getFullYear()
    }
  },
  watch: {
    menuOpen (isOpen) {
      this.$nextTick(() => {
        const main = this.$refs.mainContent
        if (!main) return

        if (isOpen) {
          main.setAttribute('inert', '')
          main.setAttribute('aria-hidden', 'true')
        } else {
          main.removeAttribute('inert')
          main.removeAttribute('aria-hidden')
        }
      })
    }
  },
  mounted () {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('keydown', this.handleMenuKeydown)
    this.handleScroll()
    this.observeReveals()
    this.observeSections()
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('keydown', this.handleMenuKeydown)
    this.observers.forEach(observer => observer.disconnect())
  },
  methods: {
    closeMenu () {
      this.menuOpen = false
    },
    toggleMenu () {
      this.menuOpen = !this.menuOpen
      if (this.menuOpen) {
        this.$nextTick(() => {
          const firstLink = this.$refs.siteNavigation && this.$refs.siteNavigation.querySelector('a')
          if (firstLink) firstLink.focus()
        })
      }
    },
    handleMenuKeydown (event) {
      if (!this.menuOpen) return

      if (event.key === 'Escape') {
        event.preventDefault()
        this.menuOpen = false
        this.$nextTick(() => this.$refs.menuToggle && this.$refs.menuToggle.focus())
        return
      }

      if (event.key !== 'Tab') return

      const links = Array.from(this.$refs.siteNavigation.querySelectorAll('a'))
      const focusable = [...links, this.$refs.menuToggle]
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    handleScroll () {
      this.scrolled = window.scrollY > 24
    },
    observeReveals () {
      if (!('IntersectionObserver' in window)) {
        document.querySelectorAll('.reveal').forEach(element => element.classList.add('is-visible'))
        return
      }
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible')
        })
      }, { threshold: 0.16 })
      document.querySelectorAll('.reveal').forEach(element => observer.observe(element))
      this.observers.push(observer)
    },
    observeSections () {
      if (!('IntersectionObserver' in window)) return
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.activeSection = entry.target.dataset.section
        })
      }, { rootMargin: '-35% 0px -55% 0px' })
      document.querySelectorAll('[data-section]').forEach(element => observer.observe(element))
      this.observers.push(observer)
    }
  }
}
</script>

<style lang="less">
@font-face {
  font-family: 'Montserrat';
  src: url('@/assets/font/Montserrat-Regular.ttf') format('truetype');
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: 'Montserrat';
  src: url('@/assets/font/Montserrat-Medium.ttf') format('truetype');
  font-weight: 500;
  font-display: swap;
}

@font-face {
  font-family: 'Montserrat';
  src: url('@/assets/font/Montserrat-SemiBold.ttf') format('truetype');
  font-weight: 600;
  font-display: swap;
}

:root {
  --ink: #020706;
  --ink-soft: #06110f;
  --paper: #f3f5f3;
  --muted: rgba(243, 245, 243, 0.64);
  --line: rgba(243, 245, 243, 0.16);
  --signal: #80d9b3;
  color-scheme: dark;
  font-family: 'Montserrat', Arial, sans-serif;
  background: var(--ink);
  scroll-behavior: smooth;
}

* { box-sizing: border-box; }

html,
body {
  width: 100%;
  min-height: 100%;
  margin: 0;
  background: var(--ink);
}

body {
  overflow-x: hidden;
  color: var(--paper);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

body:has(.menu-open) { overflow: hidden; }

button,
a { -webkit-tap-highlight-color: transparent; }

a { color: inherit; }

button:focus-visible,
a:focus-visible {
  outline: 2px solid var(--signal);
  outline-offset: 4px;
}

.skip-link {
  position: fixed;
  z-index: 1000;
  top: 1rem;
  left: 1rem;
  padding: 0.75rem 1rem;
  color: var(--ink);
  background: var(--paper);
  transform: translateY(-150%);
}

.skip-link:focus { transform: translateY(0); }

.site-header {
  position: fixed;
  z-index: 100;
  top: 1.25rem;
  right: clamp(1rem, 4vw, 4rem);
  left: clamp(1rem, 4vw, 4rem);
  height: 4.6rem;
  padding: 0.6rem 0.7rem 0.6rem 1.35rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 1.4rem;
  background: rgba(4, 12, 10, 0.28);
  backdrop-filter: blur(1.25rem) saturate(120%);
  transition: top 240ms ease, background 240ms ease, border-color 240ms ease;
}

.site-header--scrolled {
  top: 0.7rem;
  border-color: rgba(255, 255, 255, 0.14);
  background: rgba(3, 10, 8, 0.78);
}

.brand {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-decoration: none;
}

.brand-mark {
  position: relative;
  width: clamp(8.5rem, 9vw, 10rem);
  height: auto;
  display: block;
  object-fit: contain;
}

.site-nav {
  display: flex;
  align-items: center;
  gap: clamp(1.5rem, 2.6vw, 3.2rem);
}

.site-nav a {
  position: relative;
  padding: 0.6rem 0;
  color: rgba(255, 255, 255, 0.64);
  font-size: clamp(1rem, 0.95vw, 1.1rem);
  font-weight: 500;
  letter-spacing: 0.015em;
  text-decoration: none;
  transition: color 180ms ease;
}

.site-nav a::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0.2rem;
  left: 0;
  height: 1px;
  background: var(--signal);
  transform: scaleX(0);
  transition: transform 220ms ease;
}

.site-nav a:hover,
.site-nav a.active { color: #fff; }
.site-nav a.active::after { transform: scaleX(1); }

.header-contact {
  justify-self: end;
  padding: 0.9rem 1.3rem;
  border-radius: 0.9rem;
  color: var(--ink);
  background: var(--paper);
  font-size: clamp(0.92rem, 0.86vw, 1rem);
  font-weight: 600;
  text-decoration: none;
  transition: color 180ms ease, background 180ms ease, transform 180ms ease;
}

.header-contact:hover { color: var(--paper); background: var(--signal); transform: translateY(-1px); }
.menu-toggle { display: none; }

.section {
  position: relative;
  min-height: 100vh;
  min-height: 100svh;
  scroll-margin-top: 0;
  overflow: hidden;
  isolation: isolate;
}

.contact { scroll-margin-top: 0; }

.eyebrow,
.section-index {
  margin: 0;
  color: var(--signal);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.hero {
  display: grid;
  align-items: center;
  justify-items: start;
  padding: 8rem clamp(2rem, 8vw, 10rem) 4rem;
  background:
    linear-gradient(180deg, rgba(1, 7, 8, 0.08), rgba(1, 7, 8, 0.52)),
    url('@/assets/area2.webp') center / cover no-repeat;
}

.hero::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 0;
  background: radial-gradient(circle at 50% 45%, rgba(5, 17, 16, 0.04), rgba(1, 7, 7, 0.72) 82%);
}

.stars {
  position: absolute;
  z-index: -1;
  inset: -20%;
  opacity: 0.45;
  background-image: radial-gradient(#fff 0.7px, transparent 0.9px);
  background-size: 5rem 5rem;
  animation: starDrift 42s linear infinite;
}

.stars--two {
  opacity: 0.2;
  background-size: 8rem 8rem;
  animation-duration: 64s;
  animation-direction: reverse;
}

.hero-orbit {
  position: absolute;
  z-index: -1;
  right: -12vw;
  bottom: -35vw;
  width: 72vw;
  aspect-ratio: 1;
  border: 1px solid rgba(128, 217, 179, 0.18);
  border-radius: 50%;
  box-shadow: 0 0 8rem rgba(48, 168, 135, 0.1);
}

.hero-content {
  width: min(76rem, 100%);
  text-align: left;
}

.hero h1,
.statement h2,
.foundation h2,
.platforms h2,
.asia h2,
.about h2,
.contact h2 {
  margin: 0;
  font-weight: 600;
  letter-spacing: -0.07em;
  text-wrap: balance;
}

.hero h1 {
  font-size: clamp(4.5rem, 7.5vw, 8.4rem);
  line-height: 0.87;
}

.hero h1 span { display: block; }
.hero h1 span:last-child { color: var(--signal); }

h1 em,
h2 em {
  color: transparent;
  font-family: Georgia, 'Times New Roman', serif;
  font-weight: 700;
  letter-spacing: -0.04em;
  -webkit-text-stroke: 2px rgba(243, 245, 243, 0.78);
}

.hero h1 em {
  color: var(--signal);
  -webkit-text-stroke: 0;
}

.hero-copy {
  width: max-content;
  max-width: 100%;
  margin: 2rem 0 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: clamp(0.96rem, 1.08vw, 1.15rem);
  line-height: 1.55;
  white-space: nowrap;
}

.button {
  min-height: 3.7rem;
  margin-top: 2rem;
  padding: 0 1.3rem;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 2.4rem;
  border-radius: 1rem;
  font-size: clamp(0.92rem, 0.9vw, 1rem);
  font-weight: 600;
  text-decoration: none;
  transition: color 180ms ease, background 180ms ease, transform 180ms ease;
}

.button--light { color: var(--ink); background: rgba(243, 245, 243, 0.92); }
.button--light:hover { color: var(--paper); background: rgba(7, 20, 17, 0.9); transform: translateY(-2px); }

.hero-meta {
  position: absolute;
  right: clamp(1.5rem, 5vw, 6rem);
  bottom: 2rem;
  left: clamp(1.5rem, 5vw, 6rem);
  display: flex;
  justify-content: flex-start;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.statement {
  display: flex;
  align-items: center;
  padding: 8rem clamp(1.5rem, 9vw, 11rem);
  background:
    linear-gradient(90deg, rgba(1, 6, 8, 0.88), rgba(1, 6, 8, 0.1)),
    url('@/assets/area.webp') center 72% / cover no-repeat;
}

.section-shade {
  position: absolute;
  z-index: -1;
  inset: 0;
  background: linear-gradient(180deg, rgba(2, 7, 8, 0.58), transparent 32%, rgba(2, 7, 8, 0.28));
}

.statement-copy { width: min(58rem, 80vw); }
.statement h2 { font-size: clamp(4.2rem, 6.8vw, 7.6rem); line-height: 0.92; }

.foundation {
  display: flex;
  align-items: center;
  padding: 8rem clamp(1.5rem, 9vw, 11rem);
  background:
    radial-gradient(circle at 50% 50%, rgba(36, 89, 80, 0.28), rgba(1, 7, 7, 0.94) 68%),
    url('@/assets/bg11.webp') center / cover no-repeat;
}

.foundation-rings {
  position: absolute;
  z-index: -1;
  width: min(82vw, 76rem);
  aspect-ratio: 1;
  border: 1px solid rgba(128, 217, 179, 0.12);
  border-radius: 50%;
  box-shadow:
    0 0 0 10vw rgba(128, 217, 179, 0.035),
    0 0 0 20vw rgba(128, 217, 179, 0.02);
  animation: ringPulse 8s ease-in-out infinite;
}

.foundation-copy {
  width: min(58rem, 80vw);
  text-align: left;
}
.foundation h2 { font-size: clamp(4rem, 6.5vw, 7.2rem); line-height: 0.92; }
.foundation h2 span { display: block; }

.keywords {
  margin-top: 2.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.74);
  font-size: clamp(0.8rem, 1.1vw, 1rem);
  letter-spacing: 0.08em;
}

.keywords i { width: 0.25rem; height: 0.25rem; border-radius: 50%; background: var(--signal); }

.platforms {
  padding: clamp(7rem, 10vw, 11rem) clamp(1.5rem, 6vw, 7rem);
  background:
    linear-gradient(90deg, rgba(1, 7, 7, 0.9), rgba(1, 7, 7, 0.46)),
    url('@/assets/area2.webp') center / cover no-repeat;
}

.platforms::after {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 8rem 8rem;
  mask-image: linear-gradient(90deg, #000, transparent 70%);
}

.platforms-heading { max-width: 58rem; }
.platforms h2 { font-size: clamp(3.7rem, 5.5vw, 6.4rem); line-height: 0.9; }
.platform-grid {
  max-width: 96rem;
  margin: clamp(3rem, 7vw, 7rem) auto 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.platform-card {
  position: relative;
  min-height: 20rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid var(--line);
  border-radius: 1.5rem;
  background: rgba(4, 14, 12, 0.52);
  backdrop-filter: blur(1rem);
  transition: border-color 260ms ease, background 260ms ease;
}

.platform-card::before {
  content: '';
  position: absolute;
  z-index: -1;
  top: 3.6rem;
  right: -4rem;
  width: 14rem;
  height: 14rem;
  border: 1px solid rgba(128, 217, 179, 0.2);
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, rgba(128, 217, 179, 0.14), transparent 58%);
  box-shadow: inset 0 0 4rem rgba(128, 217, 179, 0.04);
}

.platform-card::after {
  content: '';
  position: absolute;
  z-index: -1;
  top: 7rem;
  right: 2rem;
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 50%;
  background: var(--signal);
  box-shadow: 0 0 1.2rem rgba(128, 217, 179, 0.85);
}

.platform-card:nth-child(2)::before { top: 2rem; right: -2rem; transform: scale(0.82); }
.platform-card:nth-child(3)::before { top: 5rem; right: -5rem; transform: scale(1.12); }
.platform-card:nth-child(4)::before { top: 3rem; right: -3rem; transform: scale(0.94); }
.platform-domain { position: relative; z-index: 1; max-width: 15rem; margin: 0; color: var(--signal); font-size: clamp(0.82rem, 0.8vw, 0.92rem); line-height: 1.55; letter-spacing: 0.1em; text-transform: uppercase; }
.platform-content { position: relative; z-index: 1; }
.platform-card h3 { max-width: 15rem; margin: 0; font-size: clamp(1.45rem, 2vw, 2.25rem); font-weight: 500; line-height: 1.05; letter-spacing: -0.045em; }
.platform-copy { max-width: 17rem; margin: 1rem 0 0; color: rgba(255, 255, 255, 0.62); font-size: 0.82rem; line-height: 1.6; }

.asia {
  min-height: 115vh;
  padding: clamp(5.75rem, 7.5vw, 8.5rem) clamp(1.5rem, 6vw, 7rem) 0;
  background: #010706;
}

.asia::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 0;
  opacity: 0.38;
  background-image: radial-gradient(rgba(255, 255, 255, 0.7) 0.7px, transparent 0.8px);
  background-size: 4.5rem 4.5rem;
}

.asia-heading { position: relative; z-index: 2; max-width: 75rem; }
.asia h2 { font-size: clamp(3.7rem, 5.7vw, 6.6rem); line-height: 0.88; }

.asia-intro {
  max-width: 52rem;
  margin-top: 1.6rem;
  display: grid;
  gap: 0.55rem;
}

.asia-intro p {
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: clamp(0.94rem, 1.15vw, 1.15rem);
  line-height: 1.55;
}
.globe {
  position: absolute;
  z-index: 1;
  right: -7vw;
  bottom: -22vw;
  width: min(90vw, 96rem);
  aspect-ratio: 1.38;
  border-radius: 50% 50% 0 0;
  background:
    linear-gradient(180deg, transparent 48%, rgba(2, 8, 7, 0.48)),
    url('@/assets/area2.webp') 62% 48% / 145% auto no-repeat;
  filter: saturate(0.8) contrast(1.05);
  box-shadow: 0 -2rem 8rem rgba(66, 196, 168, 0.1);
}

.map-point {
  position: absolute;
  min-width: 11rem;
  padding-left: 2rem;
  display: flex;
  flex-direction: column;
}

.map-point::before {
  content: '';
  position: absolute;
  top: 0.5rem;
  left: 0.35rem;
  width: 1.3rem;
  height: 1px;
  background: var(--signal);
}

.point-dot {
  position: absolute;
  top: 0.25rem;
  left: 0;
  width: 0.55rem;
  height: 0.55rem;
  border: 1px solid var(--signal);
  border-radius: 50%;
  background: #07110f;
  box-shadow: 0 0 1rem rgba(128, 217, 179, 0.7);
}

.map-point strong { font-size: 0.92rem; font-weight: 500; }
.map-point small { margin-top: 0.35rem; color: var(--muted); font-size: 0.65rem; letter-spacing: 0.09em; text-transform: uppercase; }
.point--singapore { top: 33%; left: 22%; }
.point--indonesia { top: 51%; left: 39%; }
.point--future { top: 29%; left: 60%; }

.about {
  display: flex;
  align-items: center;
  padding: 8rem clamp(1.5rem, 9vw, 11rem);
  background:
    linear-gradient(90deg, rgba(1, 8, 7, 0.95) 0%, rgba(1, 8, 7, 0.5) 55%, rgba(1, 8, 7, 0.1) 100%),
    url('@/assets/bg11.webp') center / cover no-repeat;
}

.about::after {
  content: '';
  position: absolute;
  z-index: -1;
  top: -25%;
  right: -12%;
  width: 62vw;
  aspect-ratio: 1;
  border: 1px solid rgba(128, 217, 179, 0.42);
  border-radius: 50%;
  box-shadow: 0 0 4rem rgba(128, 217, 179, 0.12);
  transform: rotate(-28deg) scaleY(0.36);
}

.about-panel { width: min(66rem, 82vw); }
.about h2 { font-size: clamp(4rem, 5.8vw, 6.8rem); line-height: 0.88; }
.about h2 span { color: var(--signal); }
.about-copy { max-width: 62rem; margin-top: 2.25rem; display: grid; gap: 1rem; }
.about-copy p { margin: 0; color: rgba(255, 255, 255, 0.82); font-size: clamp(1rem, 1.35vw, 1.3rem); line-height: 1.65; }
.contact {
  position: relative;
  min-height: 100vh;
  min-height: 100svh;
  padding: clamp(8rem, 12vw, 13rem) clamp(1.5rem, 6vw, 7rem) 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: var(--paper);
  background:
    radial-gradient(circle at 82% 12%, rgba(74, 180, 145, 0.12), transparent 24%),
    #151a18;
}

.contact-main { max-width: 88rem; }
.contact h2 { font-size: clamp(4rem, 6.5vw, 7.6rem); line-height: 0.9; }
.contact-details {
  width: min(44rem, 100%);
  margin-top: 3rem;
  padding: 0 0 1.15rem;
  display: flex;
  align-items: center;
  gap: clamp(1.5rem, 4vw, 4rem);
  border-bottom: 1px solid rgba(128, 217, 179, 0.55);
}

.contact-details span {
  color: var(--signal);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.contact-details a {
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(1rem, 1.5vw, 1.35rem);
  text-decoration: none;
  transition: color 180ms ease;
}

.contact-details a:hover { color: var(--signal); }

.footer {
  margin-top: 8rem;
  padding-top: 2rem;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  gap: 2rem;
  border-top: 1px solid var(--line);
}

.footer-brand { display: flex; align-items: center; }
.footer-brand .brand-mark { width: clamp(8rem, 9vw, 10rem); }
.footer-bottom { color: rgba(255, 255, 255, 0.5); font-size: 0.7rem; line-height: 1.5; letter-spacing: 0.08em; text-transform: uppercase; }

.reveal {
  opacity: 0;
  transform: translateY(2rem);
  transition: opacity 800ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 800ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.reveal.is-visible { opacity: 1; transform: translateY(0); }
.platform-card:nth-child(2) { transition-delay: 80ms; }
.platform-card:nth-child(3) { transition-delay: 160ms; }
.platform-card:nth-child(4) { transition-delay: 240ms; }

@keyframes starDrift { to { transform: translate3d(-3rem, 7rem, 0); } }
@keyframes ringPulse { 50% { opacity: 0.65; transform: scale(1.03); } }

@media (max-width: 1050px) {
  .platform-grid { grid-template-columns: repeat(2, 1fr); }
  .platform-card { min-height: 19rem; }
  .site-header { grid-template-columns: 1fr auto; }
  .site-nav { display: none; }
  .header-contact { margin-right: 3.5rem; }
  .menu-toggle {
    position: absolute;
    right: 0.65rem;
    width: 3rem;
    height: 3rem;
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.45rem;
    border: 0;
    color: #fff;
    background: transparent;
  }
  .menu-toggle span { width: 100%; height: 1px; background: currentColor; transition: transform 220ms ease; }
  .menu-open .menu-toggle span:first-child { transform: translateY(0.23rem) rotate(45deg); }
  .menu-open .menu-toggle span:last-child { transform: translateY(-0.23rem) rotate(-45deg); }
  .menu-open .site-nav {
    position: fixed;
    z-index: -1;
    inset: -0.7rem -1rem auto;
    min-height: 100vh;
    min-height: 100svh;
    padding: 8rem 2rem 3rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
    background: rgba(2, 9, 7, 0.98);
    overscroll-behavior: contain;
  }
  .menu-open .site-nav a { font-size: clamp(2.5rem, 8vw, 4.5rem); font-weight: 500; letter-spacing: -0.05em; }
}

@media (max-width: 700px) {
  .site-header {
    top: calc(0.7rem + env(safe-area-inset-top));
    right: max(0.75rem, env(safe-area-inset-right));
    left: max(0.75rem, env(safe-area-inset-left));
    height: 3.8rem;
    border-radius: 1.1rem;
  }
  .brand { font-size: 0.7rem; }
  .brand-mark { width: 7.25rem; height: auto; }
  .header-contact { display: none; }
  .section { min-height: 100svh; }
  .hero { min-height: 100svh; padding: 6.5rem 1.25rem 4rem; background-position: 62% center; }
  .hero-content { text-align: left; }
  .hero h1 { font-size: clamp(3rem, 13.5vw, 4.2rem); letter-spacing: -0.06em; }
  .hero-copy { width: auto; max-width: 30rem; margin: 1.35rem 0 0; font-size: 0.88rem; white-space: normal; }
  .button { min-height: 3.35rem; margin-top: 1.5rem; font-size: 0.9rem; }
  .hero-meta { right: 1.25rem; bottom: 1.35rem; left: 1.25rem; }
  .hero-meta span:last-child { display: none; }
  .statement { padding: 6rem 1.5rem; background-position: 58% 75%; }
  .statement-copy { width: 100%; }
  .statement h2 { font-size: clamp(3.2rem, 13vw, 4.6rem); }
  .foundation { padding: 6rem 1.5rem; }
  .foundation-copy { width: 100%; }
  .foundation h2 {
    max-width: 100%;
    font-size: clamp(2.75rem, 11vw, 4rem);
    letter-spacing: -0.065em;
  }
  .keywords { max-width: 21rem; margin-right: 0; margin-left: 0; gap: 0.75rem; }
  .platforms { padding: 6rem 1.25rem 4rem; }
  .platforms h2,
  .asia h2,
  .about h2,
  .contact h2 { font-size: clamp(3rem, 11.5vw, 4.3rem); }
  .platform-grid { margin-top: 2.5rem; grid-template-columns: 1fr; }
  .platform-card { min-height: 16rem; }
  .asia { min-height: 110svh; padding: 5.25rem 1.25rem 0; }
  .asia h2 { line-height: 0.92; }
  .asia-intro { max-width: 28rem; margin-top: 1.15rem; gap: 0.45rem; }
  .asia-intro p { font-size: 0.82rem; line-height: 1.45; }
  .globe { right: -36vw; bottom: -4rem; width: 150vw; }
  .map-point { min-width: 8rem; padding-left: 1.6rem; }
  .map-point::before { width: 1rem; }
  .map-point strong { font-size: 0.76rem; }
  .map-point small { font-size: 0.52rem; }
  .point--singapore { top: 37%; left: 25%; }
  .point--indonesia { top: 53%; left: 40%; }
  .point--future { top: 25%; left: 42%; }
  .about { padding: 6rem 1.5rem; background-position: 48% center; }
  .about-panel { width: 100%; }
  .about-copy { margin-top: 1.6rem; gap: 0.8rem; }
  .about-copy p { font-size: 0.92rem; line-height: 1.55; }
  .contact { padding: 6rem max(1.25rem, env(safe-area-inset-right)) max(1.5rem, env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left)); }
  .contact h2 { line-height: 0.92; }
  .contact-details { margin-top: 2rem; align-items: flex-start; flex-direction: column; gap: 0.65rem; }
  .contact-details a { font-size: 1rem; }
  .footer { margin-top: 5rem; grid-template-columns: 1fr; gap: 1.5rem; }
  .footer-brand .brand-mark { width: 7.25rem; }
}

@media (prefers-reduced-motion: reduce) {
  :root { scroll-behavior: auto; }
  *, *::before, *::after { animation: none !important; transition-duration: 0.01ms !important; }
  .reveal { opacity: 1; transform: none; }
}
</style>
